/**
 * SuperAdmin Activities — global audit feed. Reads `GET /superadmin/activities`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Search } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const actionColor = (a) => {
  if (!a) return 'var(--diis-ink-subtle)';
  if (a.includes('approv')) return '#0F766E';
  if (a.includes('reject') || a.includes('block') || a.includes('cancel') || a.includes('delete')) return '#B04A3C';
  if (a.includes('override') || a.includes('extend')) return '#B45309';
  return 'var(--diis-ink)';
};

export default function ActivitiesPage({ ctx }) {
  const { showToast } = ctx;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(100);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${endpoints.dashboard.superadminActivities}?limit=${limit}`);
      setRows(Array.isArray(data) ? data : []);
    } catch { showToast && showToast('Failed to load activities', 'err'); }
    finally { setLoading(false); }
  }, [limit, showToast]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => `${r.user_name || ''} ${r.action || ''} ${r.details || ''}`.toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <div data-testid="spa-activities-page">
      <div className="ap-eyebrow">SuperAdmin Console</div>
      <h1 className="ap-page-title">Activities</h1>
      <p className="ap-page-lede">Immutable audit trail across the whole system. Newest first.</p>

      <div className="ap-mt-3" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 420 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 12, opacity: 0.5 }} />
          <input className="ap-input" style={{ paddingLeft: 30 }} placeholder="Search user, action, detail…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="spa-activities-search" />
        </div>
        <select className="ap-input" style={{ maxWidth: 140 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))} data-testid="spa-activities-limit">
          <option value={50}>Last 50</option>
          <option value={100}>Last 100</option>
          <option value={250}>Last 250</option>
          <option value={500}>Last 500</option>
        </select>
      </div>

      <div className="ap-mt-6">
        {loading ? <div className="ap-empty">Loading activities…</div>
          : filtered.length === 0 ? <div className="ap-empty" data-testid="spa-activities-empty">{rows.length === 0 ? 'No activity yet.' : 'No entries match your search.'}</div>
          : (
          <div className="ap-table-wrap" data-testid="spa-activities-table">
            <table className="ap-table">
              <thead>
                <tr>
                  <th style={{ width: 20 }}></th>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id || `${r.timestamp}-${r.user_id}-${r.action}`} data-testid={`spa-activities-row-${r.id || r.timestamp}`}>
                    <td><Activity size={13} style={{ color: actionColor(r.action) }} /></td>
                    <td>{fmt(r.timestamp)}</td>
                    <td>
                      <div>{r.user_name || '—'}</div>
                      <div className="ap-subtle" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.14em' }}>{r.user_role || ''}</div>
                    </td>
                    <td><span style={{ color: actionColor(r.action), fontSize: 12, letterSpacing: '0.04em' }}>{(r.action || '').replace(/_/g, ' ')}</span></td>
                    <td className="ap-subtle" style={{ maxWidth: 480 }}>{r.details || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
